// src/pages/admin/AdminDashboard.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  fetchTodaySummary,
  type TodaySummary,
  fetchNextReservation,
  type NextReservation,
} from '../../api/adminDashboard';
import { Video } from 'lucide-react';
import api from '../../api/axios';
import PendingTasksPanel from '../../components/PendingTasksPanel';

// ===== 型定義 =====
type SalesData = { date: string; amount: number };

// ===== 機能グループ定義 =====
type NavCard = {
  id: string;
  icon: string | React.ReactNode;
  labelKey: string;
  path: string;
};

const GROUPS: { id: string; labelKey: string; cards: NavCard[] }[] = [
  {
    id: 'consultation',
    labelKey: 'admin.group.consultation',
    cards: [
      { id: 'reservations',  icon: '📋', labelKey: 'admin.cards.reservations.title',  path: '/admin/reservations' },
      { id: 'schedule',      icon: '🗓️', labelKey: 'admin.cards.schedule.title',       path: '/admin/schedules' },
      { id: 'auto_assign',   icon: '🤖', labelKey: 'admin.cards.auto_assign.title',    path: '/admin/auto-assign' },
      { id: 'doctors',       icon: '👨‍⚕️', labelKey: 'admin.cards.doctors.title',       path: '/admin/doctors' },
      { id: 'zoom',          icon: <Video size={18} className="text-indigo-500" />, labelKey: 'admin.cards.zoom_mode_settings.title', path: '/admin/zoom/management' },
    ],
  },
  {
    id: 'users',
    labelKey: 'admin.group.users',
    cards: [
      { id: 'users',         icon: '👤', labelKey: 'admin.cards.users.title',          path: '/admin/users' },
      { id: 'user_merge',    icon: '🔗', labelKey: 'admin.cards.user_merge.title',     path: '/admin/users/merge' },
      { id: 'invitation_qr', icon: '📲', labelKey: 'admin.cards.invitation_qr.title',  path: '/admin/users?tab=qr' },
    ],
  },
  {
    id: 'pharmacy',
    labelKey: 'admin.group.pharmacy',
    cards: [
      { id: 'medicines',     icon: '💊', labelKey: 'admin.cards.medicines.title',      path: '/admin/medicines' },
      { id: 'delivery',      icon: '📦', labelKey: 'admin.cards.delivery.title',       path: '/admin/delivery' },
      { id: 'refill',        icon: '🔄', labelKey: 'admin.cards.refill.title',         path: '/admin/refills' },
    ],
  },
  {
    id: 'marketing',
    labelKey: 'admin.group.marketing',
    cards: [
      { id: 'coupons_list',  icon: '🎟️', labelKey: 'admin.cards.coupons_list.title',   path: '/admin/coupons' },
      { id: 'coupons_new',   icon: '➕', labelKey: 'admin.cards.coupons_new.title',    path: '/admin/coupons/new' },
      { id: 'referrals',     icon: '🤝', labelKey: 'admin.cards.referrals.title',      path: '/admin/referrals' },
      { id: 'companies',     icon: '🏢', labelKey: 'admin.cards.companies.title',      path: '/admin/companies' },
      { id: 'points',        icon: '⭐', labelKey: 'admin.cards.points.title',         path: '/admin/points' },
    ],
  },
  {
    id: 'system',
    labelKey: 'admin.group.system',
    cards: [
      { id: 'ipcheck',       icon: '🛡️', labelKey: 'admin.cards.ipcheck.title',        path: '/admin/ip-check' },
      { id: 'notification_settings', icon: '🔔', labelKey: 'admin.cards.notification_settings.title', path: '/admin/notification-settings' },
      { id: 'announcement',  icon: '📢', labelKey: 'admin.cards.announcement.title',   path: '' },
    ],
  },
];

// ===== コンポーネント =====
const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // 認可チェック
  useEffect(() => {
    const token = localStorage.getItem('token');
    const role  = localStorage.getItem('role');
    if (!token) {
      navigate('/login/admin');
    } else if (role !== 'admin') {
      alert(t('admin.common.access_denied'));
      navigate('/');
    }
  }, [navigate, t]);

  // ===== データ取得 =====
  const [summary, setSummary]               = useState<TodaySummary | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [nextRes, setNextRes]               = useState<NextReservation | null>(null);
  const [loadingNext, setLoadingNext]       = useState(false);
  const [salesData, setSalesData]           = useState<SalesData[]>([]);
  const [showSalesModal, setShowSalesModal] = useState(false);
  const [pendingDeliveryCount, setPendingDeliveryCount] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        setLoadingSummary(true);
        setLoadingNext(true);
        const [summaryData, nextData, deliveriesRes, salesDataRes] = await Promise.all([
          fetchTodaySummary(),
          fetchNextReservation(),
          api.get('/admin/reservations'),
          api.get('/admin/analytics/sales?days=30').catch(() => ({ data: [] })),
        ]);
        setSummary(summaryData);
        setNextRes(nextData ?? null);
        setSalesData(salesDataRes.data || []);
        setPendingDeliveryCount(
          (deliveriesRes.data as any[]).filter(
            (r) => r.status === 'completed' && r.payment_status === 'paid' && r.shipping_status !== 'shipped',
          ).length,
        );
      } catch {
        setSummary(null);
        setNextRes(null);
      } finally {
        setLoadingSummary(false);
        setLoadingNext(false);
      }
    })();
  }, []);

  // ===== Excel エクスポート =====
  const handleDownloadExcel = async () => {
    try {
      const res = await api.get('/admin/export/reservations-xlsx', { responseType: 'blob' });
      let filename = 'reservations_paid.xlsx';
      const disposition = (res.headers['content-disposition'] || res.headers['Content-Disposition']) as string | undefined;
      if (disposition) {
        const m = disposition.match(/filename="?([^";\s]+)"?/i);
        if (m) filename = m[1];
      }
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = Object.assign(document.createElement('a'), { href: url, download: filename });
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert(t('admin.excel_export_failed'));
    }
  };

  // ===== 売上グラフ =====
  const SalesChart: React.FC<{ isModal?: boolean }> = ({ isModal = false }) => {
    if (!salesData.length) {
      return <div className="flex items-center justify-center h-full text-gray-400 text-sm">{t('admin.no_data', 'No Data')}</div>;
    }
    const maxVal = Math.max(...salesData.map((d) => d.amount), 1);
    const monthKeys = [...new Set(salesData.map((d) => d.date.substring(0, 7)))].sort();
    const COLORS = ['bg-coral', 'bg-teal-500', 'bg-amber-400', 'bg-emerald-500'];
    const getColor = (date: string, zero: boolean) => {
      if (zero) return 'bg-gray-200';
      return COLORS[monthKeys.indexOf(date.substring(0, 7)) % COLORS.length] || 'bg-coral';
    };
    return (
      <div className="flex items-end gap-[2px] w-full h-full">
        {salesData.map((d) => {
          const h = d.amount === 0 ? '4px' : `${Math.max((d.amount / maxVal) * 100, 1)}%`;
          return (
            <div key={d.date} className="group relative flex-1 flex flex-col justify-end h-full min-w-0">
              <div style={{ height: h }} className={`w-full rounded-t ${getColor(d.date, d.amount === 0)} relative`}>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block bg-black text-white text-xs p-1 rounded whitespace-nowrap z-50 pointer-events-none shadow">
                  <div className="font-bold">{d.date}</div>
                  <div>¥{d.amount.toLocaleString()}</div>
                </div>
              </div>
              {isModal && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 text-[10px] text-gray-400 whitespace-nowrap hidden group-hover:block">
                  {d.date.substring(5).replace('-', '/')}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // ===== レンダリング =====
  return (
    <div className="w-full p-4 space-y-4">

      {/* ─── ヘッダー ─── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-2xl font-bold">{t('admin.dashboard_title') || 'ダッシュボード'}</h2>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => navigate('/admin/sender-addresses')}
            className="px-4 py-2 rounded-full border border-coral/30 bg-white text-coral text-sm font-bold hover:bg-coral/5 shadow-sm transition-all active:scale-95"
          >
            {t('admin.address_management') || '発送元住所管理'}
          </button>
          <button
            onClick={handleDownloadExcel}
            className="px-4 py-2 rounded-full bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 shadow-sm transition-all active:scale-95"
          >
            {t('admin.download_excel')}
          </button>
        </div>
      </div>

      {/* ─── 行1：本日のサマリー ＋ 未処理タスク（2列） ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* 本日のサマリー */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <h3 className="text-base font-bold text-gray-800 mb-3 flex items-center gap-2">
            📅 {t('admin.cards.today_summary.title') || '本日のサマリー'}
          </h3>

          {/* 予約概要 */}
          {loadingSummary ? (
            <p className="text-sm text-gray-400">{t('admin.common.loading')}</p>
          ) : summary ? (
            <div className="grid grid-cols-2 gap-2 mb-4">
              {[
                { label: t('admin.today_total') || '本日の予約', value: summary.total, color: 'text-coral' },
                { label: t('status.reserved') || '予約中',       value: summary.reserved,  color: 'text-blue-600' },
                { label: t('status.completed') || '完了',        value: summary.completed, color: 'text-emerald-600' },
                { label: t('status.canceled') || 'キャンセル',   value: summary.canceled,  color: 'text-gray-500' },
              ].map((item) => (
                <div key={item.label} className="bg-gray-50 rounded-xl px-3 py-2">
                  <div className={`text-xl font-bold ${item.color}`}>{item.value}</div>
                  <div className="text-xs text-gray-500">{item.label}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 mb-4">{t('admin.common.fetch_failed')}</p>
          )}

          {/* 次の予約 */}
          <div className="border-t pt-3">
            <p className="text-xs font-semibold text-gray-500 mb-2">⏭️ {t('admin.today_next_reservation')}</p>
            {loadingNext ? (
              <p className="text-sm text-gray-400">{t('admin.common.searching')}</p>
            ) : nextRes ? (
              <div className="text-sm space-y-1">
                <div className="font-medium">{nextRes.date} {nextRes.time}</div>
                <div className="text-gray-600">{t('admin.today_summary_patient')}：{nextRes.user_name ?? '—'}</div>
                <div className="text-gray-600">{t('admin.today_summary_doctor')}：{nextRes.doctor_name ?? '—'}</div>
                <div className="text-gray-600">{t('admin.today_summary_service')}：{nextRes.service ?? '—'}</div>
                <div className="flex gap-2 mt-2">
                  {nextRes.zoom_join_url && (
                    <a href={nextRes.zoom_join_url} target="_blank" rel="noopener noreferrer"
                       className="px-3 py-1 text-xs rounded-full bg-coral text-white font-bold hover:opacity-90">
                      {t('doctor.enter_consultation') || '入室する'}
                    </a>
                  )}
                  {nextRes.zoom_start_url && (
                    <a href={nextRes.zoom_start_url} target="_blank" rel="noopener noreferrer"
                       className="px-3 py-1 text-xs rounded-full border border-coral text-coral font-bold hover:bg-coral/5">
                      {t('doctor.host_start') || 'Host'}
                    </a>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-400">{t('admin.no_upcoming_today')}</p>
            )}
          </div>
        </div>

        {/* 未処理タスク一覧 */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 overflow-y-auto max-h-[480px]">
          <PendingTasksPanel role="admin" />
        </div>
      </div>

      {/* ─── 行2：売上チャート（全幅） ─── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
            📊 {t('admin.cards.sales.title') || '売上サマリー（直近30日）'}
          </h3>
          <button
            onClick={() => setShowSalesModal(true)}
            className="text-xs text-gray-400 hover:text-gray-600 border border-gray-200 rounded px-2 py-0.5"
          >
            {t('admin.click_to_expand') || '拡大'}
          </button>
        </div>
        <div className="h-32">
          <SalesChart />
        </div>
      </div>

      {/* ─── 行3以下：機能別ナビカード ─── */}
      {GROUPS.map((group) => (
        <div key={group.id}>
          {/* セクションヘッダー */}
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2 px-1">
            {t(group.labelKey) || group.id}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {group.cards.map((card) => {
              // 配送系バッジ
              const badge =
                card.id === 'delivery' && pendingDeliveryCount > 0 ? pendingDeliveryCount : null;
              // path が空 = 未実装
              const isDisabled = !card.path;

              return (
                <button
                  key={card.id}
                  disabled={isDisabled}
                  onClick={() => {
                    if (isDisabled) return;
                    navigate(card.path);
                  }}
                  className={[
                    'relative bg-white rounded-2xl shadow-sm border border-gray-100 p-4',
                    'flex flex-col items-center gap-2 text-center transition-all',
                    isDisabled
                      ? 'opacity-40 cursor-not-allowed'
                      : 'hover:shadow-md hover:-translate-y-0.5 active:scale-95 cursor-pointer',
                  ].join(' ')}
                >
                  {badge !== null && (
                    <span className="absolute top-2 right-2 bg-coral text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                      {badge}
                    </span>
                  )}
                  <span className="text-2xl">{card.icon}</span>
                  <span className="text-xs font-semibold text-gray-700 leading-tight">
                    {t(card.labelKey) || card.id}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {/* ─── 売上モーダル ─── */}
      {showSalesModal && (
        <div
          className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4"
          onClick={() => setShowSalesModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-[80vh] flex flex-col p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              <h2 className="text-xl font-bold">📊 {t('admin.cards.sales.title')}</h2>
              <button onClick={() => setShowSalesModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
            </div>

            {/* チャート */}
            <div className="flex-1 bg-gray-50 rounded-xl p-4 relative min-h-0">
              {salesData.length > 0 && (
                <div className="absolute top-4 left-0 bottom-10 w-10 flex flex-col justify-between text-xs text-gray-400 text-right pr-2">
                  <span>¥{Math.max(...salesData.map((d) => d.amount)).toLocaleString()}</span>
                  <span>0</span>
                </div>
              )}
              <div className="ml-10 h-full pb-6">
                <SalesChart isModal />
              </div>
            </div>

            {/* フッター */}
            <div className="grid grid-cols-2 gap-4 mt-4 flex-shrink-0">
              <div className="bg-gray-100 rounded-xl p-3">
                <div className="text-xs text-gray-500">Total (30 Days)</div>
                <div className="text-xl font-bold">¥{salesData.reduce((s, d) => s + d.amount, 0).toLocaleString()}</div>
              </div>
              <div className="bg-green-50 rounded-xl p-3">
                <div className="text-xs text-green-600">Active Days</div>
                <div className="text-xl font-bold text-green-700">{salesData.filter((d) => d.amount > 0).length}</div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminDashboard;
